/**
 * 🎁 SPACE PROMOS - Module Promotions
 * Gestion des promotions style spatial
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, Plus, Trash2, ToggleLeft, ToggleRight, Calendar,
  Loader2, RefreshCw, Award, Zap, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte promo
const PromoCard = ({ promo, onToggle, onDelete }) => {
  const isExpired = new Date(promo.endDate) < new Date();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl p-5 border ${
        promo.isActive && !isExpired
          ? 'border-green-500/50 bg-green-500/5'
          : isExpired 
            ? 'border-gray-600 bg-gray-500/5 opacity-60'
            : 'border-gray-700 bg-[#1a1a25]'
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Gift className={`w-5 h-5 ${promo.isActive ? 'text-green-400' : 'text-gray-500'}`} />
            <h4 className="text-lg font-bold text-white">{promo.title}</h4>
            {promo.isActive && !isExpired && (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-bold">
                ACTIVE
              </span>
            )}
            {isExpired && (
              <span className="px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-xs font-bold">
                EXPIRÉE
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400 mb-3">{promo.description}</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="px-2 py-1 rounded-lg bg-cyan-500/20 text-cyan-400">
              🎯 {promo.cardType === 'all' ? 'Toutes cartes' : `Carte ${promo.cardType}`}
            </span>
            <span className="px-2 py-1 rounded-lg bg-purple-500/20 text-purple-400">
              ⚡ x{promo.multiplier} tampons
            </span>
            <span className="px-2 py-1 rounded-lg bg-gray-500/20 text-gray-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(promo.startDate).toLocaleDateString('fr-FR')} - {new Date(promo.endDate).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => onToggle(promo.id)}
            className={`p-2 rounded-lg transition-colors ${
              promo.isActive 
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
            title={promo.isActive ? 'Désactiver' : 'Activer'}
          >
            {promo.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => onDelete(promo.id)}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            title="Supprimer"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Modal création promo
const CreatePromoModal = ({ isOpen, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cardType, setCardType] = useState('all');
  const [multiplier, setMultiplier] = useState(2);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Initialiser les dates
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      setStartDate(now.toISOString().slice(0, 16));
      const end = new Date();
      end.setDate(end.getDate() + 7);
      setEndDate(end.toISOString().slice(0, 16));
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Titre et description requis');
      return;
    }

    setIsLoading(true);
    try {
      await api.createPromotion({
        title: title.trim(),
        description: description.trim(),
        cardType,
        multiplier: parseInt(multiplier),
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString()
      });
      toast.success('🎁 Promotion créée !');
      onCreated?.();
      onClose();
      // Reset form
      setTitle('');
      setDescription('');
      setCardType('all');
      setMultiplier(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur création');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border border-pink-500/30">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-400" />
            Nouvelle Promotion
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Titre</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Double tampons weekend !"
              className="bg-[#1a1a25] border-pink-500/30 text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Tous vos tampons comptent double ce weekend"
              className="bg-[#1a1a25] border-pink-500/30 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Type de carte</label>
              <select
                value={cardType}
                onChange={(e) => setCardType(e.target.value)}
                className="w-full p-2 rounded-lg bg-[#1a1a25] border border-pink-500/30 text-white"
              >
                <option value="all">Toutes les cartes</option>
                <option value="alcohol">Alcool</option>
                <option value="cbd">CBD</option>
                <option value="puffs">Puffs</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Multiplicateur</label>
              <select
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                className="w-full p-2 rounded-lg bg-[#1a1a25] border border-pink-500/30 text-white"
              >
                {[2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>x{n} tampons</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Début</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 rounded-lg bg-[#1a1a25] border border-pink-500/30 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Fin</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 rounded-lg bg-[#1a1a25] border border-pink-500/30 text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 border-gray-600">
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Module Promos principal
const SpacePromos = () => {
  const [promotions, setPromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Charger les promos
  const loadPromotions = async () => {
    setIsLoading(true);
    try {
      const data = await api.getAllPromotions?.() || [];
      setPromotions(Array.isArray(data) ? data : data.promotions || []);
    } catch (error) {
      console.error('Erreur chargement promos:', error);
      setPromotions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  // Toggle promo
  const handleToggle = async (promoId) => {
    try {
      await api.togglePromotion?.(promoId);
      toast.success('Promotion mise à jour');
      loadPromotions();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Supprimer promo
  const handleDelete = async (promoId) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;
    try {
      await api.deletePromotion?.(promoId);
      toast.success('Promotion supprimée');
      loadPromotions();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const activePromos = promotions.filter(p => p.isActive && new Date(p.endDate) >= new Date());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500">
            🎁 PROMOS
          </h1>
          <p className="text-gray-500 text-sm">Gestion des promotions</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={loadPromotions}
            variant="outline"
            className="border-pink-500/30 text-pink-400"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-pink-500 to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Promo
          </Button>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-4">
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-green-500/30 bg-green-500/5">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-400">{activePromos.length}</p>
              <p className="text-xs text-gray-500">Actives</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-pink-500/30 bg-pink-500/5">
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-pink-400" />
            <div>
              <p className="text-2xl font-bold text-pink-400">{promotions.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-purple-500/30 bg-purple-500/5">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-purple-400" />
            <div>
              <p className="text-2xl font-bold text-purple-400">
                x{Math.max(...promotions.map(p => p.multiplier || 1), 1)}
              </p>
              <p className="text-xs text-gray-500">Max multiplicateur</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des promos */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
        </div>
      ) : promotions.length === 0 ? (
        <div className="text-center py-12 backdrop-blur-xl rounded-2xl border border-gray-700 bg-[#12121a]/60">
          <Gift className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Aucune promotion</p>
          <p className="text-gray-600 text-sm mt-2">Créez votre première promo pour booster les ventes !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promotions
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
            .map((promo) => (
              <PromoCard
                key={promo.id}
                promo={promo}
                onToggle={handleToggle}
                onDelete={handleDelete}
              />
            ))}
        </div>
      )}

      {/* Modal création */}
      <CreatePromoModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={loadPromotions}
      />
    </div>
  );
};

export default SpacePromos;
